import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, Calendar, Clock, CheckCircle, AlertCircle, CreditCard, ArrowRight, Lock, XCircle, Zap, FileText, Mail, Phone, X, ShieldCheck, Star, RotateCcw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, onSnapshot, doc, getDoc, addDoc } from 'firebase/firestore';
import { StripeCheckoutModal } from '../components/StripeCheckoutModal';
import { toast } from 'react-hot-toast';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&q=80';

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  pending:                   { label: 'En attente',      color: 'text-yellow-400',    bg: 'bg-yellow-400/15 border-yellow-400/20',      icon: <Clock className="w-3 h-3" /> },
  approuve_paiement_requis:  { label: 'Paiement requis', color: 'text-[#FF5C00]',     bg: 'bg-[#FF5C00]/15 border-[#FF5C00]/20',        icon: <Zap className="w-3 h-3" /> },
  confirme:                  { label: 'Confirmé',        color: 'text-emerald-400',   bg: 'bg-emerald-400/15 border-emerald-400/20',    icon: <CheckCircle className="w-3 h-3" /> },
  approved:                  { label: 'Confirmé',        color: 'text-emerald-400',   bg: 'bg-emerald-400/15 border-emerald-400/20',    icon: <CheckCircle className="w-3 h-3" /> },
  rejected:                  { label: 'Refusé',          color: 'text-red-400',       bg: 'bg-red-400/15 border-red-400/20',            icon: <XCircle className="w-3 h-3" /> },
};

function formatDateFR(dateStr: string) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

interface PricingConfig {
  threshold: number;
  fixedFee: number;
  standardFee: number;
  percentage: number;
  launchMode: boolean;
}

interface PriceBreakdown {
  standPrice: number;
  festinFee: number;
  total: number;
  caseType: 'A' | 'B1' | 'B2';
}

const PRICING_FALLBACK: PricingConfig = { threshold: 164, fixedFee: 29, standardFee: 49, percentage: 0.30, launchMode: true };

export default function StandFestivalsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [applications, setApplications] = useState<any[]>([]);
  const [festivals, setFestivals] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [checkoutSecret, setCheckoutSecret] = useState<string | null>(null);
  const [payingAppId, setPayingAppId] = useState<string | null>(null);
  const [organizerContacts, setOrganizerContacts] = useState<Record<string, { name: string; email: string }>>({});
  const [pendingPaymentApp, setPendingPaymentApp] = useState<any | null>(null);
  const [cguAccepted, setCguAccepted] = useState(false);
  const [cancelPolicyAccepted, setCancelPolicyAccepted] = useState(false);
  const [pricingConfig, setPricingConfig] = useState<PricingConfig>(PRICING_FALLBACK);

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL || ''}/api/pricing-config`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPricingConfig(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;

    const paymentParam = searchParams.get('payment');
    const appId = searchParams.get('app_id');
    if (paymentParam === 'pending' && appId) {
      toast.success("Paiement reçu ! Votre confirmation arrive dans quelques secondes…", { icon: '⏳', duration: 7000 });
      setActiveTab('accepted');
    }

    const q = query(collection(db, 'applications'), where('applicantId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const apps = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setApplications(apps);
      setLoading(false);

      const festivalIds = Array.from(new Set(apps.map((app: any) => app.festivalId)));
      festivalIds.forEach(fid => {
        if (!festivals[fid]) {
          getDoc(doc(db, 'festivals', fid)).then(snap => {
            if (!snap.exists()) return;
            const festData = snap.data();
            setFestivals(prev => ({ ...prev, [fid]: festData }));
            // Fetch organizer contact for confirmed applications
            const ownerId = festData?.ownerId;
            if (ownerId) {
              getDoc(doc(db, 'users', ownerId)).then(userSnap => {
                if (userSnap.exists()) {
                  const u = userSnap.data();
                  setOrganizerContacts(prev => ({ ...prev, [fid]: { name: u.name || '', email: u.email || '' } }));
                }
              }).catch(() => {});
            }
          });
        }
      });
    }, () => setLoading(false));

    return () => unsubscribe();
  }, [user, searchParams]);

  const handlePayment = async (app: any) => {
    setPayingAppId(app.id);
    try {
      const idToken = await user?.getIdToken();
      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ applicationId: app.id }),
      });
      const data = await res.json();
      if (data.clientSecret) {
        setCheckoutSecret(data.clientSecret);
      } else {
        toast.error(data.error || "Impossible d'initier le paiement.");
      }
    } catch {
      toast.error('Erreur de connexion au paiement.');
    } finally {
      setPayingAppId(null);
    }
  };

  const [reapplyingId, setReapplyingId] = useState<string | null>(null);

  // Re-candidature 1 clic : recrée une candidature pending à partir d'une refusée,
  // si le festival est toujours publié et pas terminé.
  const handleReapply = async (app: any) => {
    const fest = festivals[app.festivalId];
    if (!fest || fest.status !== 'published' || (fest.endDate && new Date(fest.endDate) < new Date())) {
      toast.error('Ce festival n\'accepte plus de candidatures.');
      return;
    }
    // Une candidature active existe déjà ?
    const active = applications.find(a =>
      a.festivalId === app.festivalId && a.id !== app.id &&
      ['pending', 'approuve_paiement_requis', 'confirme'].includes(a.status)
    );
    if (active) {
      toast.error('Vous avez déjà une candidature active pour ce festival.');
      return;
    }
    setReapplyingId(app.id);
    try {
      await addDoc(collection(db, 'applications'), {
        festivalId: app.festivalId,
        festivalOwnerId: app.festivalOwnerId ?? fest.ownerId ?? null,
        standId: app.standId,
        applicantId: app.applicantId,
        festivalName: app.festivalName ?? fest.name ?? '',
        location: app.location ?? fest.location ?? '',
        dates: app.dates ?? '',
        standName: app.standName ?? '',
        applicantName: app.applicantName ?? '',
        applicantEmail: app.applicantEmail ?? '',
        format: app.format ?? 'Format de base',
        category: app.category ?? 'Autre',
        status: 'pending',
        appliedAt: new Date().toISOString(),
        reappliedFrom: app.id,
      });
      toast.success('Candidature renvoyée ! 🎉');
      setActiveTab('pending');
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la re-candidature.');
    } finally {
      setReapplyingId(null);
    }
  };

  // La notation ouvre après la fin du festival
  const canReview = (app: any) => {
    const fest = festivals[app.festivalId];
    return app.status === 'confirme' && fest?.endDate && new Date(fest.endDate) < new Date();
  };

  const getPriceBreakdown = (app: any): PriceBreakdown => {
    const detail = festivals[app.festivalId]?.standDetails?.[app.category];
    const isFree = detail?.isFree === true;
    const standPrice = (!isFree && detail && typeof detail.budget === 'number' && detail.budget > 0)
      ? detail.budget : 0;
    const activeFee = pricingConfig.launchMode ? pricingConfig.fixedFee : pricingConfig.standardFee;
    if (standPrice === 0) {
      return { standPrice: 0, festinFee: activeFee, total: activeFee, caseType: 'A' };
    }
    if (standPrice <= pricingConfig.threshold) {
      return { standPrice, festinFee: activeFee, total: standPrice + activeFee, caseType: 'B1' };
    }
    const festinFee = Math.round(standPrice * pricingConfig.percentage);
    return { standPrice, festinFee, total: standPrice + festinFee, caseType: 'B2' };
  };

  const TABS = [
    { id: 'pending',      label: 'En attente',  count: applications.filter(a => a.status === 'pending').length },
    { id: 'accepted',     label: 'Acceptées',   count: applications.filter(a => a.status === 'confirme' || a.status === 'approuve_paiement_requis').length },
    { id: 'history',      label: 'Refusées',    count: applications.filter(a => a.status === 'rejected').length },
  ];

  const filteredApps = applications.filter(app => {
    if (activeTab === 'pending')  return app.status === 'pending';
    if (activeTab === 'accepted') return app.status === 'confirme' || app.status === 'approuve_paiement_requis';
    if (activeTab === 'history')  return app.status === 'rejected';
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#FF5C00]/30 border-t-[#FF5C00] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0e12] text-white font-sans pb-24">

      {/* Header */}
      <div className="px-4 pt-8 pb-4 max-w-7xl mx-auto">
        <h1 className="text-3xl font-black mb-6 tracking-tight">Mes festivals</h1>

        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 p-1 rounded-2xl w-fit border border-white/5">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-black shadow-sm'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-black ${
                activeTab === tab.id ? 'bg-black/10 text-black' : 'bg-white/10 text-gray-400'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      <main className="px-4 max-w-7xl mx-auto">
        {filteredApps.length === 0 ? (
          <div className="text-center py-28 border border-white/5 border-dashed rounded-3xl bg-white/[0.02] flex flex-col items-center mt-4">
            <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mb-6 border border-white/10">
              {activeTab === 'pending'  && <Clock className="w-7 h-7 text-yellow-400/50" />}
              {activeTab === 'accepted' && <CheckCircle className="w-7 h-7 text-emerald-400/50" />}
              {activeTab === 'history'  && <XCircle className="w-7 h-7 text-gray-600" />}
            </div>
            <h2 className="text-xl font-bold mb-2">
              {activeTab === 'pending'  ? 'Aucune candidature en attente' :
               activeTab === 'accepted' ? 'Aucune candidature acceptée' :
               'Aucune candidature refusée'}
            </h2>
            <p className="text-gray-500 text-sm max-w-xs mx-auto mb-8">
              {activeTab === 'pending'  ? 'Explorez les festivals et postulez en 2 clics.' :
               activeTab === 'accepted' ? 'Vos candidatures acceptées apparaîtront ici.' :
               'Vos candidatures refusées seront listées ici.'}
            </p>
            {activeTab === 'pending' && (
              <button
                onClick={() => navigate('/home')}
                className="bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white px-6 py-3 rounded-full font-bold text-sm transition-all"
              >
                Explorer les festivals
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-4">
            {filteredApps.map(app => {
              const fest = festivals[app.festivalId];
              const coverImage = fest?.branding?.coverImage || FALLBACK_IMAGE;
              const status = STATUS_CONFIG[app.status] || STATUS_CONFIG['pending'];
              const needsPayment = app.status === 'approuve_paiement_requis';

              return (
                <div key={app.id} className="group flex flex-col cursor-pointer">

                  {/* Image */}
                  <div
                    className="relative aspect-[4/3] overflow-hidden rounded-2xl mb-3 bg-[#1a1a1a]"
                    onClick={() => navigate(`/festival/${app.festivalId}`)}
                  >
                    <img
                      src={coverImage}
                      alt={fest?.name || app.festivalName}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                    {/* Status badge */}
                    <div className={`absolute top-3 left-3 flex items-center gap-1.5 border backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold ${status.bg} ${status.color}`}>
                      {status.icon}
                      {status.label}
                    </div>

                    {/* Payment urgency ribbon */}
                    {needsPayment && (
                      <div className="absolute bottom-0 inset-x-0 bg-[#FF5C00] text-white text-[10px] font-black uppercase tracking-widest py-1.5 text-center">
                        ⚡ Action requise
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div onClick={() => navigate(`/festival/${app.festivalId}`)} className="flex-1">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-bold text-base leading-tight text-white group-hover:text-[#FF5C00] transition-colors truncate">
                        {fest?.name || app.festivalName}
                      </h3>
                      {app.category && (
                        <span className="text-[10px] font-bold text-gray-400 bg-white/5 border border-white/10 px-2 py-0.5 rounded-md shrink-0 whitespace-nowrap">
                          {app.category}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1 text-sm text-gray-400 mb-0.5">
                      <MapPin className="w-3 h-3 text-[#FF5C00]/60 shrink-0" />
                      <span className="truncate">{fest?.location || app.location || 'Lieu à venir'}</span>
                    </div>

                    {fest?.startDate && (
                      <div className="flex items-center gap-1 text-sm text-[#FF5C00] font-semibold">
                        <Calendar className="w-3 h-3 shrink-0" />
                        {formatDateFR(fest.startDate)} → {formatDateFR(fest.endDate)}
                      </div>
                    )}
                  </div>

                  {/* Payment block — only when action needed */}
                  {needsPayment && (
                    <div className="mt-3 bg-[#FF5C00]/8 border border-[#FF5C00]/20 rounded-2xl p-4">
                      <p className="text-xs font-black text-[#FF5C00] uppercase tracking-widest mb-3">
                        Vous êtes retenu(e) !
                      </p>
                      <div className="space-y-1.5 text-sm mb-4">
                        <div className="flex justify-between text-gray-400">
                          <span>Emplacement</span>
                          <span className="text-white font-medium">{app.format || 'Stand'}</span>
                        </div>
                        {(() => {
                          const bd = getPriceBreakdown(app);
                          const activeFee = pricingConfig.launchMode ? pricingConfig.fixedFee : pricingConfig.standardFee;
                          const standardFee = pricingConfig.standardFee;
                          return (
                            <div className="border-t border-white/10 pt-2 space-y-1.5">
                              {bd.standPrice > 0 && (
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400">Prix emplacement</span>
                                  <span className="font-bold text-white">{bd.standPrice} €</span>
                                </div>
                              )}
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-gray-400">Frais Festin</span>
                                  {pricingConfig.launchMode && (
                                    <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-1.5 py-0.5 rounded-full">Lancement</span>
                                  )}
                                </div>
                                <div className="text-right">
                                  {pricingConfig.launchMode && bd.caseType !== 'B2' && (
                                    <span className="text-gray-500 line-through text-xs mr-1">{standardFee} €</span>
                                  )}
                                  <span className="font-black text-[#FF5C00]">
                                    {bd.caseType === 'B2' ? `${Math.round(pricingConfig.percentage * 100)}%` : `${activeFee} €`}
                                  </span>
                                </div>
                              </div>
                              {bd.standPrice > 0 && (
                                <div className="flex justify-between items-center border-t border-white/8 pt-1.5">
                                  <span className="text-xs font-black text-white">Total</span>
                                  <span className="font-black text-white">{bd.total} €</span>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <button
                        onClick={() => { setCguAccepted(false); setCancelPolicyAccepted(false); setPendingPaymentApp(app); }}
                        disabled={payingAppId === app.id}
                        className="w-full bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-60 text-white py-3 rounded-xl font-black text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#FF5C00]/20"
                      >
                        <CreditCard className="w-4 h-4" />
                        {payingAppId === app.id ? 'Ouverture…' : 'Payer maintenant'}
                        {payingAppId !== app.id && <ArrowRight className="w-4 h-4" />}
                      </button>
                      <div className="flex items-center justify-center gap-1 mt-2 text-[10px] text-gray-500">
                        <Lock className="w-3 h-3" /> Paiement sécurisé via Stripe
                      </div>
                    </div>
                  )}

                  {/* Contrat + contact orga — statut confirmé */}
                  {app.status === 'confirme' && (
                    <div className="mt-3 space-y-2">
                      {/* Coordonnées organisateur débloquées */}
                      {organizerContacts[app.festivalId] && (
                        <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl space-y-2">
                          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400 flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3" /> Contact organisateur débloqué
                          </p>
                          {organizerContacts[app.festivalId].name && (
                            <p className="text-xs text-gray-300 font-semibold">{organizerContacts[app.festivalId].name}</p>
                          )}
                          {organizerContacts[app.festivalId].email && (
                            <a href={`mailto:${organizerContacts[app.festivalId].email}`}
                              className="flex items-center gap-2 text-xs text-gray-400 hover:text-white transition-colors">
                              <Mail className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                              {organizerContacts[app.festivalId].email}
                            </a>
                          )}
                        </div>
                      )}

                      {app.contractUrl ? (
                        <a
                          href={app.contractUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-bold hover:bg-emerald-500/20 transition-all"
                        >
                          <FileText className="w-4 h-4" />
                          Télécharger mon contrat
                        </a>
                      ) : (
                        <div className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-white/5 border border-white/10 text-gray-500 text-xs font-bold">
                          <FileText className="w-3.5 h-3.5" />
                          Contrat en cours de génération…
                        </div>
                      )}
                      {canReview(app) && (
                        <button
                          onClick={() => navigate(`/review/${app.id}`)}
                          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#FF5C00]/10 border border-[#FF5C00]/20 text-[#FF5C00] text-sm font-bold hover:bg-[#FF5C00]/20 transition-all"
                        >
                          <Star className="w-4 h-4" />
                          Noter ce festival
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/festival/${app.festivalId}`)}
                        className="w-full py-2.5 rounded-xl border border-white/8 bg-white/3 hover:bg-white/8 text-sm font-bold text-white transition-all"
                      >
                        Voir le festival
                      </button>
                    </div>
                  )}

                  {/* View festival button — autres statuts */}
                  {!needsPayment && app.status !== 'confirme' && (
                    <div className="mt-3 space-y-2">
                      {app.status === 'rejected' && festivals[app.festivalId]?.status === 'published' &&
                        (!festivals[app.festivalId]?.endDate || new Date(festivals[app.festivalId].endDate) > new Date()) && (
                        <button
                          onClick={() => handleReapply(app)}
                          disabled={reapplyingId === app.id}
                          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#FF5C00] hover:bg-[#e05000] disabled:opacity-50 text-white text-sm font-black transition-all"
                        >
                          <RotateCcw className="w-4 h-4" />
                          {reapplyingId === app.id ? 'Envoi…' : 'Recandidater en 1 clic'}
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/festival/${app.festivalId}`)}
                        className="w-full py-3 rounded-xl border border-white/8 bg-white/3 hover:bg-white/8 text-sm font-bold text-white transition-all"
                      >
                        Voir le festival
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Modale CGU + politique d'annulation avant paiement */}
      {pendingPaymentApp && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#141518] border border-white/10 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-white/8">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#FF5C00]" />
                <h2 className="font-black text-white text-base">Avant de payer</h2>
              </div>
              <button onClick={() => setPendingPaymentApp(null)} className="text-gray-500 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Récap montant — deux lignes */}
              {(() => {
                const bd = getPriceBreakdown(pendingPaymentApp);
                const festName = festivals[pendingPaymentApp.festivalId]?.name || '';
                const activeFee = pricingConfig.launchMode ? pricingConfig.fixedFee : pricingConfig.standardFee;
                return (
                  <div className="bg-white/3 border border-white/8 rounded-xl p-4 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Récapitulatif</p>
                      <p className="text-xs font-bold text-white">{festName}</p>
                    </div>
                    {bd.standPrice > 0 && (
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-300">Prix emplacement</p>
                        <p className="font-bold text-white">{bd.standPrice} €</p>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm text-gray-300">Frais de service Festin</p>
                        {pricingConfig.launchMode && bd.caseType !== 'B2' && (
                          <span className="text-[10px] font-black text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-1.5 py-0.5 rounded-full">-{Math.round((1 - activeFee / pricingConfig.standardFee) * 100)}%</span>
                        )}
                      </div>
                      <div className="text-right flex items-baseline gap-1.5">
                        {pricingConfig.launchMode && bd.caseType !== 'B2' && (
                          <p className="text-xs text-gray-500 line-through">{pricingConfig.standardFee} €</p>
                        )}
                        <p className="font-black text-[#FF5C00]">
                          {bd.caseType === 'B2' ? `${bd.festinFee} € (${Math.round(pricingConfig.percentage * 100)}%)` : `${activeFee} €`}
                        </p>
                      </div>
                    </div>
                    {bd.standPrice > 0 && (
                      <div className="flex items-center justify-between border-t border-white/10 pt-2.5">
                        <p className="text-sm font-black text-white">Total à payer</p>
                        <p className="text-xl font-black text-white">{bd.total} €</p>
                      </div>
                    )}
                    {bd.standPrice === 0 && (
                      <div className="flex items-center justify-between border-t border-white/10 pt-2.5">
                        <p className="text-sm font-black text-white">Total</p>
                        <p className="text-xl font-black text-[#FF5C00]">{bd.festinFee} €</p>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Politique d'annulation */}
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Politique d'annulation</p>
                <div className="bg-white/3 border border-white/8 rounded-xl p-4 space-y-2 text-xs text-gray-300 leading-relaxed">
                  <div className="flex gap-2"><span className="text-emerald-400 font-bold shrink-0">✓</span><span><b>Annulation &gt; 30 jours avant :</b> remboursement 100%</span></div>
                  <div className="flex gap-2"><span className="text-yellow-400 font-bold shrink-0">~</span><span><b>Annulation 15–30 jours avant :</b> remboursement 50%</span></div>
                  <div className="flex gap-2"><span className="text-red-400 font-bold shrink-0">✕</span><span><b>Annulation &lt; 15 jours :</b> aucun remboursement</span></div>
                  <div className="flex gap-2 pt-1 border-t border-white/8"><span className="text-emerald-400 font-bold shrink-0">✓</span><span><b>Annulation par le festival :</b> remboursement 100% sous 5 jours</span></div>
                </div>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className={`mt-0.5 w-5 h-5 rounded border-2 shrink-0 flex items-center justify-center transition-all ${cguAccepted ? 'bg-[#FF5C00] border-[#FF5C00]' : 'border-white/20 group-hover:border-white/40'}`}
                    onClick={() => setCguAccepted(v => !v)}>
                    {cguAccepted && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <span className="text-sm text-gray-300 leading-snug" onClick={() => setCguAccepted(v => !v)}>
                    J'ai lu et j'accepte les{' '}
                    <a href="/terms" target="_blank" className="text-[#FF5C00] hover:underline" onClick={e => e.stopPropagation()}>
                      Conditions Générales d'Utilisation
                    </a>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className={`mt-0.5 w-5 h-5 rounded border-2 shrink-0 flex items-center justify-center transition-all ${cancelPolicyAccepted ? 'bg-[#FF5C00] border-[#FF5C00]' : 'border-white/20 group-hover:border-white/40'}`}
                    onClick={() => setCancelPolicyAccepted(v => !v)}>
                    {cancelPolicyAccepted && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <span className="text-sm text-gray-300 leading-snug" onClick={() => setCancelPolicyAccepted(v => !v)}>
                    J'ai lu et j'accepte la politique d'annulation et de remboursement ci-dessus
                  </span>
                </label>
              </div>

              {/* Bouton confirmer */}
              <button
                disabled={!cguAccepted || !cancelPolicyAccepted || payingAppId === pendingPaymentApp.id}
                onClick={() => { handlePayment(pendingPaymentApp); setPendingPaymentApp(null); }}
                className="w-full bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-40 disabled:cursor-not-allowed text-white py-3.5 rounded-xl font-black text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#FF5C00]/20"
              >
                <Lock className="w-4 h-4" />
                Confirmer et payer {getPriceBreakdown(pendingPaymentApp).total} €
                <ArrowRight className="w-4 h-4" />
              </button>

              <p className="text-center text-[10px] text-gray-500 flex items-center justify-center gap-1">
                <Lock className="w-3 h-3" /> Paiement 100% sécurisé via Stripe
              </p>
            </div>
          </div>
        </div>
      )}

      {checkoutSecret && (
        <StripeCheckoutModal
          clientSecret={checkoutSecret}
          onClose={() => setCheckoutSecret(null)}
        />
      )}
    </div>
  );
}
